import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { api, ApiError } from "./api";
import { mergeRunList, newerRun, runKey } from "./run-state";
export { runKey } from "./run-state";

export type User = {
  id: string;
  username: string;
  display_name: string;
  email: string | null;
};
export type RunStatus =
  | "queued"
  | "running"
  | "awaiting_input"
  | "completed"
  | "failed"
  | "cancelled";
export type RunMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  status?: string;
};
export type RunEvent = {
  id: string;
  type: string;
  name?: string;
  content: string;
  status: string;
  created_at: string;
  details?: unknown;
};
export type RunSummary = {
  id: string;
  title: string;
  status: RunStatus;
  version: number;
  cancel_requested: boolean;
  created_at: string;
  updated_at: string;
};
export type Run = RunSummary & {
  messages: RunMessage[];
  events: RunEvent[];
  result: Record<string, unknown> | null;
  error: string | null;
  session_id: string | null;
};
export const statusLabels: Record<RunStatus, string> = {
  queued: "排队中",
  running: "判读中",
  awaiting_input: "等待补充",
  completed: "已完成",
  failed: "执行失败",
  cancelled: "已取消",
};
export const isActive = (status?: RunStatus) =>
  status === "running" || status === "queued";
type SessionContext = {
  user: User | null;
  loading: boolean;
  authError: Error | null;
  runs: RunSummary[];
  runsLoading: boolean;
  runsError: Error | null;
  connection: "connecting" | "connected" | "disconnected";
  loginOpen: boolean;
  openLogin: () => void;
  closeLogin: () => void;
  setUser: (user: User | null) => void;
  refreshRuns: () => void;
};
const Session = createContext<SessionContext | null>(null);
export function useSession() {
  const value = useContext(Session);
  if (!value) throw new Error("SessionProvider is missing");
  return value;
}
export function SessionProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const location = useLocation();
  const [loginOpen, setLoginOpen] = useState(false);
  const [connection, setConnection] =
    useState<SessionContext["connection"]>("connecting");
  const auth = useQuery({
    queryKey: ["session"],
    queryFn: async ({ signal }) => {
      try {
        return (await api<{ user: User }>("/auth/me", { signal })).user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
    refetchInterval: (query) =>
      query.state.status === "error" ? 5_000 : false,
  });
  const user = auth.data ?? null;
  const currentOwner = useRef(user?.id);
  currentOwner.current = user?.id;
  const list = useQuery({
    queryKey: ["agent-runs", user?.id],
    enabled: Boolean(user),
    queryFn: ({ signal }) => api<{ items: RunSummary[] }>("/runs", { signal }),
    structuralSharing: (previous, incoming) =>
      mergeRunList(
        previous as { items: RunSummary[] } | undefined,
        incoming as { items: RunSummary[] },
      ),
    refetchInterval: connection === "disconnected" ? 10_000 : false,
  });
  const selectedId = location.pathname.match(/^\/runs\/([^/]+)$/)?.[1];
  const setUser = (next: User | null) => {
    currentOwner.current = next?.id;
    void client.cancelQueries({ queryKey: ["session"] });
    client.removeQueries({ queryKey: ["agent-runs"] });
    client.removeQueries({ queryKey: ["agent-run"] });
    client.removeQueries({ queryKey: ["api"] });
    client.setQueryData(["session"], next);
    setLoginOpen(false);
  };
  useEffect(() => {
    if (!user) return;
    let closed = false;
    const isCurrent = () => !closed && currentOwner.current === user.id;
    setConnection("connecting");
    const stream = new EventSource(
      `/api/v1/events${selectedId ? `?run_id=${encodeURIComponent(selectedId)}` : ""}`,
    );
    stream.onopen = () => {
      if (isCurrent()) setConnection("connected");
    };
    stream.onerror = () => {
      if (!isCurrent()) return;
      setConnection("disconnected");
      void client.invalidateQueries({ queryKey: ["session"] });
    };
    stream.addEventListener("connection_error", () => {
      if (isCurrent()) setConnection("disconnected");
    });
    stream.addEventListener("snapshot", (event) => {
      if (!isCurrent()) return;
      let snapshot: {
        runs: RunSummary[];
        run: Run | null;
      };
      try {
        snapshot = JSON.parse(event.data);
      } catch {
        setConnection("disconnected");
        return;
      }
      client.setQueryData<{ items: RunSummary[] }>(
        ["agent-runs", user.id],
        (previous) => mergeRunList(previous, { items: snapshot.runs }),
      );
      if (snapshot.run) {
        const run = snapshot.run;
        client.setQueryData<Run>(runKey(user.id, run.id), (previous) =>
          newerRun(previous, run),
        );
      }
      setConnection("connected");
    });
    stream.addEventListener("session_expired", () => {
      if (!isCurrent()) return;
      stream.close();
      currentOwner.current = undefined;
      client.removeQueries({ queryKey: ["agent-runs"] });
      client.removeQueries({ queryKey: ["agent-run"] });
      client.removeQueries({ queryKey: ["api"] });
      client.setQueryData(["session"], null);
      setLoginOpen(true);
    });
    return () => {
      closed = true;
      stream.close();
    };
  }, [user?.id, selectedId, client]);
  return (
    <Session.Provider
      value={{
        user,
        loading: auth.isPending,
        authError: auth.error,
        runs: list.data?.items ?? [],
        runsLoading: Boolean(user) && list.isPending,
        runsError: list.error,
        connection,
        loginOpen,
        openLogin: () => setLoginOpen(true),
        closeLogin: () => setLoginOpen(false),
        setUser,
        refreshRuns: () => {
          void client.invalidateQueries({ queryKey: ["agent-runs"] });
        },
      }}
    >
      {children}
    </Session.Provider>
  );
}
